import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useClientTable } from "../../../components/table/tableFeatures";
import { type Selection } from "react-aria-components";
import { FaChevronDown } from "react-icons/fa6";
import { Trans, useTranslation } from "react-i18next";
import { css, cx } from "../../../../styled-system/css";
import { Flex, HStack } from "../../../../styled-system/jsx";
import { formField } from "../../../../styled-system/recipes";
import { Menu, MenuButton, MenuItem } from "../../../components/core/basic/Menu";
import { Select, SelectItem } from "../../../components/fields/base/Select";
import { SearchInput } from "../../../components/fields/base/TextField";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { useAdHistoryEntityTypes } from "../api/useAdHistoryEntityTypes";
import { useAdvertisementHistory } from "../api/useAdvertisementHistory";
import { DevErrorDisplay } from "../../../components/core/blocks/DevErrorDisplay";
import { formatCurrency } from "../../../utils/formatNumber";
import { snakeCaseToSentenceCase, toTitleCase } from "../../../utils/formatText";
import ExternalProductLink from "../../../components/custom/merchandise/ExternalProductLink";
import { AD_STATES, AD_STATUSES } from "../../../components/core/blocks/AdPlan/constants";
import type { AdvertisementHistoryEntry } from "../../../zodios/apiDefinitions/api/advertisementHistory";
import type { TSelector } from "../../../i18n/types";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import type { TFunction } from "i18next";

const columnHelper = createColumnBuilder<AdvertisementHistoryEntry>();

const DEFAULT_ENTITY_TYPES = ["Advertisement"];

const CURRENCY_FIELDS = new Set(["bid", "preview_bid", "default_bid", "budget"]);

function isCurrencyField(field: string) {
  return CURRENCY_FIELDS.has(field) || field.endsWith("_bid") || field.endsWith("_budget");
}
const HIDDEN_FIELDS = new Set(["preview_bid"]);

function toLabelMap(obj: Record<string, { value: number; label: TSelector }>): Record<number, TSelector> {
  return Object.fromEntries(Object.values(obj).map((v) => [v.value, v.label]));
}

const LABELED_FIELDS: Record<string, Record<number, TSelector>> = {
  state: toLabelMap(AD_STATES),
  ad_status: toLabelMap(AD_STATUSES),
};

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
function formatChangeValue(value: unknown, field: string, t: TFunction<any, any>): React.ReactNode {
  if (value == null) return "-";
  if (isCurrencyField(field)) return formatCurrency(value as number | string);
  const labelMap = LABELED_FIELDS[field];
  if (labelMap && typeof value === "number") {
    const sel = labelMap[value];
    return sel ? t(sel) : String(value);
  }
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return toTitleCase(snakeCaseToSentenceCase(value));
  if (typeof value === "object" && !Array.isArray(value)) {
    return Object.entries(value as Record<string, unknown>).map(([k, v]) => (
      <div key={k}>
        {toTitleCase(snakeCaseToSentenceCase(k))}: {formatChangeValue(v, k, t)}
      </div>
    ));
  }
  return JSON.stringify(value);
}

export const AdvertisementHistory = ({ advertisementId }: { advertisementId: number }) => {
  const { t } = useTranslation(["advertising"]);
  const fmt = useDateFormatter("shortDateTime");
  const [days, setDays] = useState(7);
  const [selectedEntityTypes, setSelectedEntityTypes] = useState(DEFAULT_ENTITY_TYPES);
  const entityTypesQuery = useQuery(useAdHistoryEntityTypes());
  const entityTypeOptions = entityTypesQuery.data?.entity_types ?? [];
  const adHistoryQuery = useQuery(useAdvertisementHistory(advertisementId, days, selectedEntityTypes));

  const daysOptions = useMemo(
    () => [
      { id: "1", label: t(($) => $.advertising["Last 1 day"]) },
      { id: "3", label: t(($) => $.advertising["Last 3 days"]) },
      { id: "7", label: t(($) => $.advertising["Last 7 days"]) },
      { id: "14", label: t(($) => $.advertising["Last 14 days"]) },
      { id: "30", label: t(($) => $.advertising["Last 30 days"]) },
    ],
    [t],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("timestamp", {
        id: "timestamp",
        header: t(($) => $.advertising["Time"]),
        cell: ({ getValue }) => fmt.format(new Date(getValue())),
        size: 180,
      }),
      columnHelper.accessor("entity_type", {
        id: "entity_type",
        header: t(($) => $.advertising["Entity Type"]),
        cell: ({ getValue }) => getValue(),
        size: 160,
      }),
      columnHelper.accessor("entity_name", {
        id: "entity_name",
        header: t(($) => $.advertising["Entity"]),
        cell: ({ row }) => {
          const name = row.original.entity_name;
          if (!name) return "-";
          const entityType = row.original.entity_type;
          if (entityType === "ProductAdvertisement") {
            const [asin, sku] = name.split(" - ");
            return (
              <HStack gap="small" alignItems="center">
                {asin} ({sku}) <ExternalProductLink asin={asin} />
              </HStack>
            );
          }
          return name;
        },
        size: 300,
      }),
      columnHelper.accessor("action", {
        id: "action",
        header: t(($) => $.advertising["Action"]),
        cell: ({ getValue }) => toTitleCase(getValue()),
        size: 100,
      }),
      columnHelper.accessor("full_name", {
        id: "full_name",
        header: t(($) => $.advertising["User"]),
        cell: ({ row }) => {
          const fullName = row.original.full_name;
          const email = row.original.email;
          if (!fullName && !email) return t(($) => $.advertising["System"]);
          return (
            <div>
              {fullName && <div>{fullName}</div>}
              {email && <div className={css({ fontSize: "medium" })}>{email}</div>}
            </div>
          );
        },
        size: 200,
      }),
      columnHelper.display({
        id: "changes",
        header: t(($) => $.advertising["Changes"]),
        cell: ({ row }) => {
          const visibleChanges = row.original.changes.filter((c) => !HIDDEN_FIELDS.has(c.field));
          if (!visibleChanges.length) return "-";
          return (
            <div>
              {visibleChanges.map((change, i) => (
                <div key={i}>
                  <strong>{toTitleCase(snakeCaseToSentenceCase(change.field))}</strong>:{" "}
                  {change.from != null ?
                    <Trans
                      t={t}
                      i18nKey={($) => $.advertising["<from/> to <to/>"]}
                      components={{
                        from: <>{formatChangeValue(change.from, change.field, t)}</>,
                        to: <>{formatChangeValue(change.to, change.field, t)}</>,
                      }}
                    />
                  : formatChangeValue(change.to, change.field, t)}
                </div>
              ))}
            </div>
          );
        },
        size: 400,
      }),
    ],
    [t, fmt],
  );

  const { state, setters, initialState } = usePersistedTableState({
    persistKey: "advertisementHistory",
    initialState: {
      pagination: { pageIndex: 0, pageSize: 10 },
      sorting: [{ id: "timestamp", desc: true }],
    },
  });

  const table = useClientTable({
    data: adHistoryQuery.data,
    columns,
    initialState,
    state,
    ...setters,
    meta: {
      showSkeleton: adHistoryQuery.isFetching && !adHistoryQuery.data,
    },
  });

  if (adHistoryQuery.isError) {
    return (
      <DevErrorDisplay
        userMessage={t(($) => $.advertising["Error loading history data"])}
        error={adHistoryQuery.error}
      />
    );
  }

  return (
    <>
      <HStack padding={"small"} justifyContent={"space-between"}>
        <HStack gap={"large"} alignItems={"center"}>
          <SearchInput
            aria-label={t(($) => $.advertising["Search history"])}
            isExpandable
            css={{ flex: "0 1 350px" }}
            value={table.state.globalFilter}
            onChange={table.setGlobalFilter}
          />
          <Select
            aria-label={t(($) => $.advertising["Time range"])}
            selectedKey={String(days)}
            onSelectionChange={(key) => setDays(Number(key))}
            items={daysOptions}
            css={{ width: "300px" }}
          >
            {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
          </Select>
          <EntityTypesFilter
            options={entityTypeOptions}
            selected={selectedEntityTypes}
            onChange={setSelectedEntityTypes}
          />
        </HStack>
        <RT8Pagination table={table} />
      </HStack>
      {selectedEntityTypes.length === 0 || (adHistoryQuery.data && adHistoryQuery.data.length === 0) ?
        <Flex
          justifyContent="center"
          alignItems="center"
          paddingY="xlarge"
          className={css({ color: "grey", fontSize: "medium" })}
        >
          {selectedEntityTypes.length === 0 ?
            t(($) => $.advertising["Select at least one entity type to view history."])
          : t(($) => $.advertising["No history entries found for the selected filters."])}
        </Flex>
      : <Table table={table} className={css({ minWidth: "100%", "& td": { paddingY: "small" } })} />}
    </>
  );
};

type EntityTypesFilterProps = {
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
};

function EntityTypesFilter({ options, selected, onChange }: EntityTypesFilterProps) {
  const { t } = useTranslation(["advertising"]);
  const selectedKeys: Selection = useMemo(() => new Set(selected), [selected]);
  const classes = formField();

  return (
    <MenuButton
      buttonProps={{
        // Styled as an input box like the Select beside it, not as a button
        unstyled: true,
        isDisabled: options.length === 0,
        "aria-label": t(($) => $.advertising["Filter by entity type"]),
        className: cx(
          classes.inputBox,
          css({ paddingY: 0, width: "200px", textWrap: "nowrap", _enabled: { cursor: "pointer" } }),
        ),
      }}
      label={
        <>
          <span className={cx(classes.input, css({ display: "flex", alignItems: "center", gap: "small" }))}>
            <span className={css({ color: "text.label", fontWeight: "bold" })}>
              {t(($) => $.advertising["Entities"])}
            </span>
            <span>{t(($) => $.advertising["{count} selected"], { count: selected.length })}</span>
          </span>
          <span aria-hidden="true" className={css({ fontSize: "xxsmall" })}>
            <FaChevronDown />
          </span>
        </>
      }
      popoverProps={{ className: css({ width: "200px" }) }}
    >
      <Menu
        aria-label={t(($) => $.advertising["Entities"])}
        selectionMode="multiple"
        selectedKeys={selectedKeys}
        onSelectionChange={(selection: Selection) => {
          if (selection === "all") {
            onChange(options);
          } else {
            onChange([...selection].map(String));
          }
        }}
        items={options.map((option) => ({ id: option, name: option }))}
      >
        {(item) => <MenuItem id={item.id}>{item.name}</MenuItem>}
      </Menu>
    </MenuButton>
  );
}
